export interface CareCanvas {
    id: string;
    name: string;
    final_image: string;
}

export interface IpdAdmission {
    advised?: boolean;
    include_procedures?: boolean;
    notes?: string;
}

export interface Procedure {
    id: string;
    name: string;
    date?: string;
    notes?: string;
}

export interface OpSpec {
    eye?: {
        value?: string;
    };
    sph?: {
        id: string;
        value?: string;
        custom?: string;
    };
    cyl?: {
        id: string;
        value?: string;
        custom?: string;
    };
    axis?: {
        id: string;
        value?: string;
        custom?: string;
    };
    add?: {
        id: string;
        value?: string;
        custom?: string;
    };
    dva?: {
        id: string;
        value?: string;
        custom?: string;
    };
    nva?: {
        id: string;
        value?: string;
        custom?: string;
    };
}

export interface OpSpecElement extends OpSpec {
    id: string;
}

export interface OpIop {
    eye?: {
        value?: string;
    };
    nct?: {
        id: string;
        value?: string;
        custom?: string;
    };
    gat?: {
        id: string;
        value?: string;
        custom?: string;
    };
    cct?: {
        id: string;
        value?: string;
        custom?: string;
    };
    ciop?: {
        id: string;
        value?: string;
        custom?: string;
    };
}

export interface OpIopElement extends OpIop {
    id: string;
}

export interface OpVision {
    eye?: {
        value?: string;
    };
    ucdva?: {
        id: string;
        value?: string;
        custom?: string;
    };
    ucnva?: {
        id: string;
        value?: string;
        custom?: string;
    };
    ph?: {
        id: string;
        value?: string;
        custom?: string;
    };
    bcdva?: {
        id: string;
        value?: string;
        custom?: string;
    };
    bcnva?: {
        id: string;
        value?: string;
        custom?: string;
    };
}

export interface OpVisionElement extends OpVision {
    id: string;
}

export type IMilestoneCategory =
    | 'ek-7084767782'
    | 'ek-1025408070'
    | 'ek-9916604356'
    | 'ek-9994397806'

export type IMilestoneStatus =
    | 'achieved'
    | 'under_observation'
    | 'delayed'
    | 'achieved_delay'
    | 'achieved_early'
    | 'achieved_unknown';

export interface IMilestoneHistory {
    id: string;
    notes?: string;
    status?: IMilestoneStatus;
    age?: { unit?: string; value: string };
    categoryId: IMilestoneCategory;
    name: string;
    category: string;
}

export enum VACCINATION_STATUS {
    DUE = 'Due',
    GIVEN = 'Given',
    MISSED = 'Missed',
    PATIENT_REFUSED = 'Patient Refused',
}

export type I_VACCINATION_STATUS =
    | VACCINATION_STATUS.DUE
    | VACCINATION_STATUS.GIVEN
    | VACCINATION_STATUS.MISSED
    | VACCINATION_STATUS.PATIENT_REFUSED;


export interface IVaccineStatus {
    id: string; // vaccine id
    status?: I_VACCINATION_STATUS;
    date?: string;
    brandId?: string;
    batchNumber?: string;
    notes?: string;
    diseaseId: string;
    doseNo: number;
    ageGroup?: string;
    name: string;
}

export interface DentalProcedure {
    entity_id?: string;
    id: string;
    name: string;
    teeth_ids?: string[];
    surfaces?: Surface[];
    visits?: string;
    start_on?: string;
    remark?: string;
    service_id?: string;
    conducted_by?: string;
    assisted_by?: string;
    index?: number;
}

export interface Surface {
    id: string;
    name: string;
}

export enum DENTAL_ENTITY_TYPE {
    EXAMINATION = 'examination',
    PROCEDURE = 'procedure',
}

export interface DentalExamination {
    entity_id?: string;
    id: string;
    name: string;
    teeth_id?: string;
    surfaces?: Surface[];
    since?: string;
    remark?: string;
    group_id?: string;
    type?: DENTAL_ENTITY_TYPE; // examination or procedure
    index?: number;
}

export interface Attachment {
    title: string;
    description: string;
    link: string;
    file_type: string;
    templateId?: string;
}

export interface RefRange {
    low?: string | null;
    high?: string | null;
}

export interface Unit {
    name: string;
    id: string;
    refRange?: RefRange;
}

export interface Interpretation {
    value: string;
    id: string;
    eka_id?: string;
    name_list?: string[];
}

export interface AllUnitsEntity {
    name: string;
    id: string;
    refRange?: RefRange;
}
export interface RefRange {
    low?: string | null;
    high?: string | null;
}

export interface LabVitalEntity {
    id: string;
    name: string;
    unit_dislay_name?: string;
    value?: string;
    interpretation?: Interpretation;
    date?: string;
    remark?: string;
    unit?: Unit;
    all_units?: AllUnitsEntity[] | null;
    is_panel?: boolean;
}

export interface DocNetworkEntity {
    name: Name;
    specialized: string;
    pic: string;
    _id: string;
    username: string;
    points: number;
    area: string;
    city: string;
    location: string;
    exprience?: string;
}

export interface LabRefer {
    s_name: string;
    name: string;
    id?: number;
    group_id?: string;
    patientid: string;
    note?: string;
    eka_note?: string;
    refer_to_eka: boolean;
    campaign: boolean;
    created_at: string;

    lab: DocNetworkEntity[];
}

export interface Phone {
    code: string;
    value: string;
}

export interface Name {
    l: string;
    f: string;
}

export interface DocEntity {
    id?: string;
    referred_doctor_url?: string;
    name?: Name;
    phone?: Phone;
    clinic?: DoctorReferClinicAddress;
    note?: string;
    s_name?: string;
    s_id?: string;
    type?: 'doctor' | 'speciality';
}

export type DoctorReferClinicAddress = {
    _id?: string;
    name?: string;
    line?: string;
    landmark?: string;
    city?: string;
    state?: string;
    city_id?: string;
    place_id?: string;
    pincode?: string;
    coordinates?: string;
    gplacesid?: string;
};

export interface CreateReferalData {
    s_name?: string;
    name?: string;
    id?: number;
    docid?: string;
    group_id?: string;
    patientid?: string;
    note?: string;
    eka_note?: string;
    refer_to_eka?: boolean;
    campaign?: boolean;
    created_at?: string;
    clinic?: DoctorReferClinicAddress;
    doc: DocEntity[];
    visitId?: string;
    referred_doctor_url?: string;
    referred_doctor_oid?: string;
    doc_name?: string;
    doc_phone?: string;
    is_appreciated?: boolean;
    prescriptionId?: string;
    type?: string;
    archived?: boolean;
}

export interface Notes {
    id: string;
    text: string;
    parsedText: string;
}

export interface AdvicesEntity {
    id: string;
    text: string;
    parsedText: string;
}

export interface Followup {
    date?: string;
    appointmentId?: string;
    optionId?: string; // for analytic purpose
    notes?: string;
}

export interface LabTestsEntityOrProceduresEntity {
    name: string;
    id: string;
    remark?: string;
    book?: boolean;
    test_on?: string;
    repeat_on?: string;
    type?: string;
    content?: string[];
    vendor_names?: string[];
    vendor_ids?: string[];
    hxng_only?: boolean;
}

export interface InfusionRate {
    total_volume?: MedDose;
    infusion_time?: MedDose;
    iv_drop_rate?: MedDose;
    iv_rate?: MedDose;
}

export interface InjectionsEntity {
    name: string;
    id: string;
    generic_name: string;
    form: string;
    df_id?: string;
    dosage_form?: string;
    route: string;
    dose: MedDose;
    infusion_rate: InfusionRate;
    frequency: Frequency;
    duration: Duration;
    timing: string;
    instructions?: string;
    tapering_dose?: InjectionsEntity[];
    added_drug?: InjectionsEntity[];
    actual_name?: string;
}

export interface Frequency {
    type?: string; // either code or onceEvery or SOS or toContinue or custom
    code?: string;
    onceEvery?: {
        value: string;
        unit: string;
    };
    custom?: string;
    time_split?: MedDose[];
}

export interface MedDose {
    id?: string;
    value?: string | number;
    unit?: string;
    custom?: string;
    timing?: Date | string;
}

export interface MedDoseInfo {
    dose?: MedDose;
    area?: { name: string; id: string };
    frequency?: Frequency;
    timing?: string;
    duration?: Duration;
    quantity?: {
        custom?: string;
    };
    instruction?: string;
    start_from?: {
        text: string;
        value: string;
        unit: string;
    };
    to_day?: {
        text: string;
        value: string;
        unit: string;
    };
}

export type OPTION_SOURCE =
    | 'COPILOT'
    | 'GENIE'
    | 'MDB'
    | 'SUBSTITUTE'
    | 'API_SEARCH'
    | 'SUGGESTED'
    | 'CUSTOM'
    | 'SA'
    | 'SUGGESTED/COMPLEMENTARY'
    | 'BORROW'
    | 'MAGIC_BOX';

export type LABEL_TYPE =
    | 'AS_EMPTY'
    | 'AS_INLINE_SEARCH'
    | 'AS_INLINE_EMPTY'
    | 'AS_SEARCH'
    | 'CHIPS'
    | 'ADD_DRUG_DIALOG'
    | 'BULK_DRUG_DIALOG'
    | 'GD_SUGGESTED'
    | 'GD_SEARCH_REPLACE'
    | 'GD_ADD_MEDICINES'
    | 'PAD_ADD_DRUG_DIALOG'
    | 'PAD_FIX_STRIP'
    | 'SNACKING_QUESTIONS'
    | 'SNACKING_QUESTIONS_NEW'
    | 'POPUP';

export type PxElSourceTrack = {
    track?: {
        label?: LABEL_TYPE;
        index?: number;
        source?: OPTION_SOURCE | string;
        columnIndex?: number;
    };
};

export enum HXNG_MAPPED {
    YES = 'yes',
    NO = 'no',
    UNKNOWN = '',
}

export enum HXNG_STATUS {
    AVAILABLE = 'available',
    NOT_AVAILABLE = 'not available',
    UNKNOWN = 'unknown',
}

export type MedicineCategory = 'advice' | 'device' | 'nad';

export interface MedicationsEntity
    extends MedDoseInfo,
        // only for tracking purposes
        PxElSourceTrack {
    name: string;
    id: string;

    // returned by MDB
    // not editable, should be shown on label in cards, near delete icon
    // added laterly, fallback to form
    dosage_form?: string;

    // this is the form input prompt to user, when df_id is not found
    // associated keys => dose.custom, basically !dose.id
    // not related to mdb
    form?: string;

    product_type?: string;
    generic_name?: string;
    generic_id?: string;
    df_id?: string;

    tapering_dose?: MedDoseInfo[];
    soa?: boolean;

    // contains the original name from mdb, before renaming
    actual_name?: string;

    is_otc?: boolean;
    manufacturer_name?: string;
    manufacturer_id?: string;
    med_cat?: MedicineCategory;
    hxng_mapped?: HXNG_MAPPED;
    hxng_status?: HXNG_STATUS;
}

export interface SymDiaStatus {
    code: string;
    display: string;
}

export interface Severity {
    code: string;
    display: string;
}

export interface Duration {
    value?: string | number;
    unit?: string;
    custom?: string;
}

export interface SymDiaProperty {
    name: string;
    selection?: SelectionEntity[];
}
export interface SelectionEntity {
    value: string;
    unit?: string;
    id: string;
}

export interface DiagnosisEntityOrSymptomsEntity {
    name: string;
    id: string;
    duration?: Duration;
    severity?: Severity;
    status?: SymDiaStatus; // only is diagnosis // TODO create new type
    remark?: string;
    isLocal?: boolean; // If created local

    // server properties
    properties?: { [k: string]: SymDiaProperty };
    icd10_code?: string;
    icd10_name?: string;
}

export interface GenieItem {
    meta?: {
        updatedAt?: number;
        isLocal?: number; // 0 or 1
        fCount?: number; // used how many times
        do_not_purge?: boolean;
    };
}

export interface PadFDFormElement extends GenieItem {
    id: string;
    name?: string;
    value?: string;
    icd10_name?: string;
    icd10_code?: string;
}

export type PadDForm = {
    id: string;
    name: string;
    elements?: PadFDFormElement[];
};

export interface VitalsEntity {
    name: string;
    dis_name: string;
    id: string;
    value: Value;
    interpretation?: string;
    /**
     * @deprecated Use isoDate
     */
    date?: string;
    isoDate?: string;
    remark?: string;
    locked?: boolean;
}
export interface Value {
    qt: string;
    unit: string; // same as be dis_unit
    code?: string; // unit code, refer mdb/v1/vitals api for more details
    code_id?: string; // unit code id, refer mdb/v1/vitals api for more details
    safe?: Safe;
}

export interface Safe {
    low?: string;
    high?: string;
    type: 'Numeric' | 'string' | string;
    is_decimal_allowed?: boolean;
    normal_value?: string;
}

export interface ExaminationEntity {
    id: string;
    name: string;
    notes?: string;
}

export type PatientMedicalCondition = {
    id: string;
    name: string;
    status?: string;
    reportedAt?: string;
    since?: {
        custom: string;
    };
    notes?: string;
};

export type FamilyHistory = {
    id: string;
    name: string;
    who?: string;
    reportedAt?: string;
    notes?: string;
    status?: string;
};

export type LifestyleHabits = {
    id: string;
    name: string;
    frequency?: {
        custom: string;
    };
    reportedAt?: string;
    since?: {
        custom: string;
    };
    notes?: string;
    status?: string;
};

export type RecentTravelHistory = {
    id: string;
    name: string;
    notes?: string;
    reportedAt?: string;
    status?: string;
};

export type CurrentMedications = {
    id: string;
    name: string;
    reportedAt?: string;
    frequency?: {
        custom: string;
    };
    since?: {
        custom: string;
    };
    notes?: string;
    status?: string;
};

export type DrugAllergy = {
    id: string;
    name: string;
    reportedAt?: string;
    since?: {
        custom: string;
    };
    notes?: string;
    status?: string;
    generic_id?: string;
    generic_name?: string;
};

export type FoodOthersAllergy = {
    id: string;
    name: string;
    reportedAt?: string;
    since?: {
        custom: string;
    };
    notes?: string;
    status?: string;
};

export type PastProcedures = {
    id: string;
    name: string;
    reportedAt?: string;
    on?: {
        mm: string;
        yyyy: string;
    };
    notes?: string;
    status?: string;
};

export type OtherMedicalHistory = {
    id: string;
    name: string;
    reportedAt?: string;
    notes?: string;
    status?: string;
};

export type MHC = {
    patientMedicalConditions?: PatientMedicalCondition[];
    familyHistory?: FamilyHistory[];
    lifestyleHabits?: LifestyleHabits[];
    recentTravelHistory?: RecentTravelHistory[];
    currentMedications?: CurrentMedications[];
    drugAllergy?: DrugAllergy[];
    foodOtherAllergy?: FoodOthersAllergy[];
    pastProcedures?: PastProcedures[];
    otherMedicalHistory?: OtherMedicalHistory[];
};

export interface Tool {
    dyform?: PadDForm[];
    language?: string;
    diagnosis?: DiagnosisEntityOrSymptomsEntity[];
    symptoms?: DiagnosisEntityOrSymptomsEntity[];
    medications?: MedicationsEntity[];
    injections?: InjectionsEntity[];
    labTests?: LabTestsEntityOrProceduresEntity[];
    followup?: Followup;
    advices?: AdvicesEntity[];
    prescriptionNotes?: Notes;
    privateNotes?: Notes;
    medicalHistory?: {
        patientHistory?: MHC;
        examinations?: ExaminationEntity[];
        vitals?: VitalsEntity[];
    };
    meta?: {
        templateId?: string;
    };
    refer?: CreateReferalData;
    labRefer?: LabRefer;
    labVitals?: LabVitalEntity[];
    attachment?: Attachment;
    dentalExaminations?: DentalExamination[];
    dentalProcedures?: DentalProcedure[];
    administeredVaccines?: IVaccineStatus[];
    milestones?: IMilestoneHistory[];
    allMilestones?: IMilestoneHistory[];
    vaccinationHistory?: IVaccineStatus[];
    opVision?: OpVisionElement[];
    opIop?: OpIopElement[];
    opCurrentSpec?: OpSpecElement[];
    opSubjectiveRefraction?: OpSpecElement[];
    opAutoRefraction?: OpSpecElement[];
    opFinalPrescription?: OpSpecElement[];
    procedures?: Procedure[];
    ipdAdmission?: IpdAdmission;
    careCanvas?: CareCanvas[];
}
